import React, { memo, useCallback, useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Heading,
  HStack,
  IconButton,
  Skeleton,
  useToastContext,
  VStack,
} from '@rtkwlf/fenrir-react';
import type {
  OrganizationPreferenceRequestBody,
  SubjectTemplates,
} from '@rtkwlf/notification-service';
import { faXmark } from '@rtkwlf/pro-solid-svg-icons/faXmark';
import { useTranslation } from 'react-i18next';

import { useGetNotificationPreferences } from '@apis/notification-service/queries/use-get-notification-preferences';
import { useGetSubjectTemplates } from '@apis/notification-service/queries/use-get-subject-templates';
import { useUpsertNotificationPreferences } from '@apis/notification-service/queries/use-upsert-notification-preferences';
import { DetailsPageHeader } from '@packages/components/page/DetailsPageHeader';
import { PageToolbar } from '@packages/components/page/page-toolbar';
import { PageWrapper } from '@packages/components/page/PageWrapper';
import { Box, Stack, Typography } from '@packages/fenrir/components';
import { Alert } from '@packages/fenrir/components/alert';
import { Snackbar } from '@packages/fenrir/components/snackbar';
import { ErrorLoading } from '@packages/fenrir/feedback';
import { PageContainerRoot } from '@packages/fenrir/page-blocks/page-container/page-container-root';
import { PagePlain } from '@packages/fenrir/pages/page-plain';

import DeliveryMethodGroup from './components/delivery-method-group/delivery-method-group';
import NotificationFrequencyGroup from './components/notification-frequency-group/notification-frequency-group';
import SubjectLineCustomizationDropdown from './components/subject-line-customization-dropdown/subject-line-customization-dropdown';

export const PreferencesPage = memo(() => {
  const { t } = useTranslation(['notifications', 'common']);
  const { addToast } = useToastContext();
  const {
    data: preferences,
    isLoading,
    isError,
    refetch,
  } = useGetNotificationPreferences();

  const {
    data: templatesResponse,
    isLoading: isTemplatesLoading,
    isError: isTemplatesError,
    refetch: refetchTemplates,
  } = useGetSubjectTemplates();

  const {
    mutate: upsertPreferences,
    isPending: isUpdating,
    isSuccess: isUpsertSuccess,
    isError: isUpsertError,
  } = useUpsertNotificationPreferences();

  // Fetch preferences and subject templates when component loads
  useEffect(() => {
    refetch();
    refetchTemplates();
  }, [refetch, refetchTemplates, preferences, isUpsertSuccess, addToast, t]);

  const [displaySuccessToast, setDisplaySuccessToast] = useState(false);
  const [displayErrorToast, setDisplayErrorToast] = useState(false);
  const SNACKBAR_AUTO_HIDE_DURATION = 5000;
  const [prefs, setPrefs] = useState(preferences);
  const [isPreferencesEdited, setIsPreferencesEdited] = useState(false);

  function updatePreferences(): void {
    if (prefs !== undefined) {
      upsertPreferences(
        { input: prefs as OrganizationPreferenceRequestBody },
        {
          onSuccess: () => {
            refetch();
            refetchTemplates();
            setIsPreferencesEdited(false);
          },
        },
      );
      setDisplayErrorToast(true);
      setDisplaySuccessToast(true);
    }
  }

  const handleEmailChange = useCallback(
    (isEmailEnabled: boolean) => {
      if (!preferences) return;

      const updatedPreferences = {
        ...preferences,
        isEmailEnabled,
      };
      setIsPreferencesEdited(true);
      setPrefs(updatedPreferences);
    },
    [preferences],
  );
  const handleItsmChange = useCallback(
    (isITSMEnabled: boolean) => {
      if (!preferences) return;

      const updatedPreferences = {
        ...preferences,
        isITSMEnabled,
      };
      setIsPreferencesEdited(true);
      setPrefs(updatedPreferences);
    },
    [preferences],
  );

  const handleDigestModeChange = useCallback(
    (isDigestEnabled: boolean) => {
      if (!preferences) return;

      const updatedPreferences = {
        ...preferences,
        isDigestEnabled,
      };
      setIsPreferencesEdited(true);
      setPrefs(updatedPreferences);
    },
    [preferences],
  );

  const handlePrefixChange = useCallback(
    (subjectPrefix: SubjectTemplates | undefined) => {
      if (!preferences) return;
      const updatedPreferences = {
        ...preferences,
        subjectPrefix,
      };
      setIsPreferencesEdited(true);
      setPrefs(updatedPreferences);
    },
    [preferences],
  );

  const handleSuffixChange = useCallback(
    (subjectSuffixes: SubjectTemplates[] | undefined) => {
      if (!preferences) return;
      const updatedPreferences = {
        ...preferences,
        subjectSuffixes,
      };
      setIsPreferencesEdited(true);
      setPrefs(updatedPreferences);
    },
    [preferences],
  );

  if (isLoading || isTemplatesLoading) {
    return (
      <PagePlain>
        <PageWrapper
          gap="default"
          width="full"
          height="full"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Skeleton height="1.5rem" width="1.5rem" />
        </PageWrapper>
      </PagePlain>
    );
  }

  if (isError || isTemplatesError) {
    return (
      <PagePlain>
        <PageContainerRoot>
          <ErrorLoading
            reloadOnClick={() => {
              window.location.reload();
            }}
            actionLink={{
              label: t('returnToHomepage'),
              to: '/',
            }}
          />
        </PageContainerRoot>
      </PagePlain>
    );
  }
  function handleReload(): void {
    window.location.reload();
  }

  // Normalize preferences to ensure all boolean fields are always boolean
  const normalizedPreferences = preferences
    ? {
        ...preferences,
        isDigestEnabled: preferences.isDigestEnabled ?? false,
        isEmailEnabled: preferences.isEmailEnabled ?? false,
        isITSMEnabled: preferences.isITSMEnabled ?? false,
      }
    : undefined;

  const successCloseAction = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <IconButton
        size="default"
        aria-label="close"
        onClick={() => setDisplaySuccessToast(false)}
        style={{ color: 'inherit', zIndex: 1500 }}
      >
        <FontAwesomeIcon icon={faXmark} />
      </IconButton>
    </Box>
  );

  const errorCloseAction = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <IconButton
        size="default"
        aria-label="close"
        onClick={() => setDisplayErrorToast(false)}
        style={{ color: 'inherit', zIndex: 1500 }}
      >
        <FontAwesomeIcon icon={faXmark} />
      </IconButton>
    </Box>
  );

  return (
    <PagePlain>
      <PageToolbar.Root>
        <DetailsPageHeader
          paddingBottom="zero"
          header={
            <Heading level="1" styledAs="heading3">
              {t('common:organizationProfile')}
            </Heading>
          }
        >
          <PageToolbar.RequestHelp />
        </DetailsPageHeader>
      </PageToolbar.Root>
      <PageWrapper gap="default" width="full">
        <Stack sx={{ gap: '.5rem', mb: '2rem' }}>
          <Typography variant="h4">{t('notificationPreferences')}</Typography>
          <Typography variant="body2" color="textSecondary">
            {t('notificationPreferencesDescription')}
          </Typography>
        </Stack>
        <Stack sx={{ gap: '.5rem', mb: '1rem' }}>
          <Typography variant="h5">{t('reportsCategory')}</Typography>
          <Typography variant="body2" color="textSecondary">
            {t('reportsCategoryDescription')}
          </Typography>
        </Stack>
        <VStack gap="default" width="full" marginBottom="medium">
          <DeliveryMethodGroup
            preferences={normalizedPreferences}
            onEmailChange={handleEmailChange}
            onItsmChange={handleItsmChange}
            isUpdating={isUpdating}
          />

          <NotificationFrequencyGroup
            preferences={normalizedPreferences}
            onDigestModeChange={handleDigestModeChange}
            isUpdating={isUpdating}
          />
          <HStack>
            <SubjectLineCustomizationDropdown
              preferences={normalizedPreferences}
              templates={templatesResponse?.templates}
              onSuffixChange={handleSuffixChange}
              onPrefixChange={handlePrefixChange}
              suffixHelperText={
                normalizedPreferences?.isITSMEnabled
                  ? t('subjectLineCustomization.helperText')
                  : ''
              }
            />
          </HStack>
          <HStack alignItems="center" justifyContent="flex-end" width="full">
            <Button
              isDisabled={!isPreferencesEdited}
              onClick={handleReload}
              variant="secondary"
            >
              {t('cancelButton')}
            </Button>
            <Button
              onClick={updatePreferences}
              variant="primary"
              isDisabled={!isPreferencesEdited}
            >
              {t('saveButton')}
            </Button>
          </HStack>
        </VStack>
        <Snackbar
          onClose={() => setDisplaySuccessToast(false)}
          open={isUpsertSuccess && displaySuccessToast}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
        >
          <Alert
            action={successCloseAction}
            severity="success"
            variant="standard"
          >
            {t('successMessage')}
          </Alert>
        </Snackbar>
        <Snackbar
          onClose={() => setDisplayErrorToast(false)}
          open={isUpsertError && displayErrorToast}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
        >
          <Alert action={errorCloseAction} severity="error" variant="standard">
            {t('errorMessage')}
          </Alert>
        </Snackbar>
      </PageWrapper>
    </PagePlain>
  );
});

export default PreferencesPage;
